import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'common/components/Button';
import { IGroupFragment, ISkillRequirementFragment } from 'graphql/types';
import { RouteLink } from 'route/components/RouteLink';
import { GroupSkillPortfolioListItem } from 'user/components/GroupSkillPortfolioListItem/GroupSkillPortfolioListItem';

import { GroupSkillPortfolioItemDrawer } from './GroupSkillPortfolioItemDrawer';

interface IProps {
    group: IGroupFragment;
}

export const SkillsView = ({ group }: IProps) => {
    const [translate] = useTranslation();
    const [selectedRequirement, setSelectedRequirement] =
        useState<ISkillRequirementFragment | null>(null);

    const handleClose = useCallback(() => {
        setSelectedRequirement(null);
    }, []);

    const skillMatrixQuery = useMemo(() => {
        const groupFilterValue = [{ id: group.id, name: group.name }];

        try {
            const encoded = window.btoa(
                JSON.stringify({
                    skillGroups: groupFilterValue,
                    groupIds: groupFilterValue,
                })
            );

            return `?filters=${encoded}`;
        } catch {
            return '';
        }
    }, [group.id, group.name]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <Typography variant="h4">
                    {translate('groupSkills.title')}
                </Typography>

                <Button
                    component={RouteLink}
                    query={skillMatrixQuery}
                    size="small"
                    to="SKILLS_MATRIX"
                    variant="contained"
                >
                    {translate('groupSkills.toSkillMatrix')}
                </Button>
            </Box>

            {(group.skillRequirements || []).map(
                (req) =>
                    req.skill && (
                        <GroupSkillPortfolioListItem
                            groupUserCount={group.userCount}
                            key={req.skill.id}
                            skill={req.skill}
                            skillRequirement={{
                                minAbsolute: req.minAbsolute,
                                minPercent: req.minPercent,
                                hasMandatoryCertificates:
                                    req.hasMandatoryCertificates,
                                compliancy: req.compliancy,
                            }}
                            onClick={() => setSelectedRequirement(req)}
                        />
                    )
            )}

            <GroupSkillPortfolioItemDrawer
                groupId={group.id}
                groupName={group.name}
                groupUserCount={group.userCount}
                open={!!selectedRequirement}
                skillRequirement={selectedRequirement}
                onClose={handleClose}
            />
        </>
    );
};
